import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

import { Loading } from './../../../Common/Loading'
import { InstructorBase } from './../../../Layouts/Instructor/V2/InstructorBase'
import { Confirm } from './../../../Common/Modals/Confirm'

import { LogErrors } from './../../../../utilities/LogErrors'
import { ValidateSession } from './../../../../utilities/BL/Sessions'
import { useFloatMessage, MESSAGE_TYPE } from './../../../Providers/FloatMessage'
import {
    GetInstructorsForAssign,
    GetCoursesForAssign,
    AssignLicensesToInstructor,
    GetAssignedLicensesForCourse
} from './../../../../utilities/BL/Leader/Licenses'
import { WEBPAGES } from './../../../../utilities/EL/WebPages'
import { ROLES } from './../../../../utilities/EL/Roles'

const AssignLicenses = () => {
    const navigate = useNavigate()
    const { showMessage } = useFloatMessage()

    const [isLoading, setIsLoading] = useState(true)
    const [isSubmitting, setIsSubmitting] = useState(false)

    const [instructors, setInstructors] = useState([])
    const [courses, setCourses] = useState([])

    const [selectedInstructor, setSelectedInstructor] = useState('')
    const [selectedCourse, setSelectedCourse] = useState('')
    const [quantity, setQuantity] = useState('')

    const [showConfirmationModal, setShowConfirmationModal] = useState(false)
    const [confirmData, setConfirmData] = useState(null)

    useEffect(() => {
        async function ValidateAndInit() {
            try {
                const tmpSession = await ValidateSession()
                if (tmpSession?.success && tmpSession.data?.user?.userStatus === 2) {
                    // Cargar instructores y cursos de forma paralela
                    const [resInsts, resCourses] = await Promise.all([
                        GetInstructorsForAssign(),
                        GetCoursesForAssign()
                    ])

                    if (resInsts.success) {
                        setInstructors(resInsts.data || [])
                    } else {
                        showMessage({ type: MESSAGE_TYPE.BAD, text: resInsts.message || 'No fue posible cargar los instructores.' })
                    }

                    if (resCourses.success) {
                        setCourses(resCourses.data || [])
                    } else {
                        showMessage({ type: MESSAGE_TYPE.BAD, text: resCourses.message || 'No fue posible cargar los cursos.' })
                    }

                    setIsLoading(false)
                } else {
                    navigate('/confirm')
                }
            } catch (error) {
                await LogErrors('ValidateAndInit - Leaders AssignLicenses', error)
                navigate(`/?logout=${process.env.REACT_APP_LOGOUT_PARAMETER}`)
            }
        }
        ValidateAndInit()
    }, [navigate, showMessage])

    const handleQuantityChange = (e) => {
        const value = e.target.value
        // Permitir únicamente caracteres numéricos (dígitos)
        if (/^\d*$/.test(value)) {
            setQuantity(value)
        }
    }

    const handleInstructorChange = (value) => {
        setSelectedInstructor(value)
        // Un curso ya elegido (y su cantidad prellenada) corresponde al instructor anterior
        setSelectedCourse('')
        setQuantity('')
    }

    const handleCourseChange = async (value) => {
        setSelectedCourse(value)

        if (!selectedInstructor || !value) return

        try {
            const response = await GetAssignedLicensesForCourse(selectedInstructor, value)

            if (response.success && response.data?.licenses !== undefined && response.data?.licenses !== null) {
                setQuantity(String(response.data.licenses))
            }
            // Si no devuelve un valor, el cuadro de texto se queda como está, sin valor.
        } catch (error) {
            await LogErrors('handleCourseChange - AssignLicenses UI', error)
        }
    }

    const HandleAssignClick = () => {
        if (!selectedInstructor || !selectedCourse || !quantity) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Por favor complete todos los campos.' })
            return
        }

        const qtyInt = parseInt(quantity, 10)
        if (isNaN(qtyInt) || qtyInt <= 0 || String(qtyInt) !== quantity) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'La cantidad de licencias debe ser un número entero positivo.' })
            return
        }

        const inst = instructors.find(i => String(i.idCenterUser) === String(selectedInstructor))
        const crs = courses.find(c => String(c.id) === String(selectedCourse))

        setConfirmData({
            quantity: qtyInt,
            instructorName: inst ? inst.instructorName : '',
            courseName: crs ? crs.name : ''
        })
        setShowConfirmationModal(true)
    }

    const ExecuteAssign = async () => {
        try {
            setIsSubmitting(true)
            const response = await AssignLicensesToInstructor(selectedInstructor, selectedCourse, quantity)

            // Cerrar el modal de confirmación en ambos casos
            setShowConfirmationModal(false)
            setConfirmData(null)

            if (response.success) {
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: 'Licencias asignadas correctamente.'
                })
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: response.message || 'No fue posible asignar las licencias.'
                })
            }
            return { success: true }
        } catch (error) {
            await LogErrors('ExecuteAssign - AssignLicenses UI', error)
            setShowConfirmationModal(false)
            setConfirmData(null)
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'No fue posible asignar las licencias.'
            })
            return { success: true }
        } finally {
            setIsSubmitting(false)
        }
    }

    return isLoading ? (
        <Loading />
    ) : (
        <InstructorBase urlToGo={'/leader/licenses'} page={WEBPAGES.LEADER.ZTUDENTS} role={ROLES.LEADER}>
            <section className='container Instructor__Instructors Container-FullHeight'>
                <p className='Instructor__Title Instructor__Title--Center'>Asignar Licencias</p>

                <div className='container mt-5 Tables--Shadow Tables--Background p-4' style={{ borderRadius: '15px' }}>
                    <p className='Instructor__Subtitle Instructor__Subtitle--Center mb-4 border-bottom pb-2 fw-bold text-uppercase' style={{ letterSpacing: '0.05em' }}>
                        Asignar Licencias a Instructor
                    </p>

                    <div className='d-flex flex-wrap align-items-end justify-content-center' style={{ gap: '20px' }}>
                        <div style={{ flex: '1 1 250px', maxWidth: '300px', minWidth: '200px' }}>
                            <label className='Form__Label mb-2 d-block'>Selecciona un Instructor</label>
                            <select
                                className='Instructor__Agendas__Filters__Select m-0'
                                style={{ width: '100%' }}
                                value={selectedInstructor}
                                onChange={(e) => handleInstructorChange(e.target.value)}
                                disabled={isSubmitting}
                            >
                                <option value=''>-- Elige un Instructor --</option>
                                {instructors.map(inst => (
                                    <option key={inst.idCenterUser} value={inst.idCenterUser}>
                                        {inst.instructorName} ({inst.username})
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div style={{ flex: '1 1 250px', maxWidth: '300px', minWidth: '200px' }}>
                            <label className='Form__Label mb-2 d-block'>Selecciona un curso</label>
                            <select
                                className='Instructor__Agendas__Filters__Select m-0'
                                style={{ width: '100%' }}
                                value={selectedCourse}
                                onChange={(e) => handleCourseChange(e.target.value)}
                                disabled={isSubmitting || !selectedInstructor}
                            >
                                <option value=''>-- Elige un Curso --</option>
                                {courses.map(course => (
                                    <option key={course.id} value={course.id}>
                                        {course.name} (Nivel {course.level})
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div style={{ flex: '0 0 120px' }}>
                            <label className='Form__Label mb-2 d-block'>Número de licencias</label>
                            <input
                                type='text'
                                className='Instructor__Agendas__Filters__Select m-0'
                                style={{ width: '100%' }}
                                value={quantity}
                                onChange={handleQuantityChange}
                                placeholder='Cantidad'
                                disabled={isSubmitting}
                            />
                        </div>

                        <div style={{ flex: '0 0 130px' }}>
                            <button
                                type='button'
                                className='Btn__Ztudia--Primary w-100'
                                style={{ padding: '9.9px 20px', borderRadius: '8px', border: '1px solid #e61b72', height: '42.7px' }}
                                onClick={HandleAssignClick}
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? 'Asignando...' : 'Asignar'}
                            </button>
                        </div>
                    </div>
                </div>

                {showConfirmationModal && confirmData && (
                    <Confirm
                        message={`¿Estás segur@ de asignar ${confirmData.quantity} licencias al Instructor ${confirmData.instructorName} para el curso ${confirmData.courseName}?`}
                        actionToYesButton={ExecuteAssign}
                        textYesButton='Sí, asignar licencias'
                        textNoButton='No'
                        setShowModal={setShowConfirmationModal}
                        setConfirmData={setConfirmData}
                    />
                )}
            </section>
        </InstructorBase>
    )
}

export { AssignLicenses };
